import React, {useEffect, useMemo, useState} from "react";
import {UseTranslateResult} from "@tolgee/react";
import {useDebouncedValue, useFormContext} from "hooks";
import {Path} from "react-hook-form";
import {PasswordInput, PasswordInputProps} from "view/components/Form";
import {
  MIN_PASSWORD_LENGTH,
  PASSWORD_UNSUPPORTED_CHARACTERS_ERROR,
  PASSWORD_UNSUPPORTED_CHARS_REGEX,
  PASSWORD_VALIDATION_REGEX,
  PASSWORD_VALIDATION_REGEX_MAP,
  PASSWORD_VALIDATION_RULE_MESSAGE,
  PASSWORD_VALIDATION_RULE_TRANSLATION_KEY,
  PASSWORD_VALIDATION_RULES,
  PasswordValidationRule,
} from "view/components/PasswordInputs/utils";
import {Popper, Typography, useTheme} from "@material-ui/core";
import FlexBox from "view/components/Layout/FlexBox";
import CheckIcon from "@material-ui/icons/Check";
import {grey} from "@material-ui/core/colors";
import RemoveIcon from "@material-ui/icons/Remove";
import Alert from "view/components/Alert";
import useStyles from "./styles";

type PasswordFields = {password: string; confirmPassword: string};

type PasswordInputsProps = {
  // Present only on localized pages; without it the English copy is kept.
  t?: UseTranslateResult["t"];
  gap?: number;
};

const PasswordInputs = <T extends PasswordFields>({
  t,
  gap = 3,
}: PasswordInputsProps) => {
  const {palette} = useTheme();
  const classes = useStyles();
  const [debouncedPassword, setDebouncedPassword] = useDebouncedValue();
  const [anchorEl, setAnchorEl] = useState<HTMLInputElement | null>(null);

  const {
    watch,
    formState: {errors},
  } = useFormContext<T>();

  const password = watch("password" as Path<T>);
  const password2 = watch("confirmPassword" as Path<T>);

  useEffect(() => {
    setDebouncedPassword(password);
  }, [password]);

  const metRules = useMemo<PasswordValidationRule[]>(() => {
    const result: PasswordValidationRule[] = [];

    if (PASSWORD_VALIDATION_REGEX_MAP.HasUpper.test(debouncedPassword)) {
      result.push(PasswordValidationRule.UpperCase);
    }
    if (PASSWORD_VALIDATION_REGEX_MAP.HasLower.test(debouncedPassword)) {
      result.push(PasswordValidationRule.LowerCase);
    }
    if (PASSWORD_VALIDATION_REGEX_MAP.HasNumber.test(debouncedPassword)) {
      result.push(PasswordValidationRule.Digit);
    }
    if (PASSWORD_VALIDATION_REGEX_MAP.HasSpecial.test(debouncedPassword)) {
      result.push(PasswordValidationRule.SpecialChar);
    }
    if (debouncedPassword.length >= MIN_PASSWORD_LENGTH) {
      result.push(PasswordValidationRule.MinLen);
    }

    return result;
  }, [debouncedPassword]);

  const commonProps: PasswordInputProps = {
    style: {marginBottom: 0},
    defaultVisible: false,
    toggleIconClassname: classes.toggleIcon,
  };

  const renderPopperContent = () => {
    return PASSWORD_VALIDATION_RULES.map((rule) => {
      const isRuleSatisfied = metRules.includes(rule);
      return (
        <FlexBox fullWidth alignCenter gap={0.5} key={rule}>
          {isRuleSatisfied ? (
            <CheckIcon className={classes.popperIcon} color="primary" />
          ) : (
            <RemoveIcon className={classes.popperIcon} htmlColor={grey[700]} />
          )}
          <Typography
            variant="h5"
            style={{color: isRuleSatisfied ? palette.primary.main : grey[700]}}
          >
            {t
              ? t(PASSWORD_VALIDATION_RULE_TRANSLATION_KEY[rule], {
                  count: MIN_PASSWORD_LENGTH,
                })
              : PASSWORD_VALIDATION_RULE_MESSAGE[rule]}
          </Typography>
        </FlexBox>
      );
    });
  };

  const isPopperOpen = !!anchorEl;

  const unsupportedCharactersMessage = t
    ? t("common.validation.password_unsupported_characters")
    : "Password contains unsupported characters or spaces";

  const passwordError =
    errors.password?.message === PASSWORD_UNSUPPORTED_CHARACTERS_ERROR
      ? unsupportedCharactersMessage
      : null;

  const isPasswordsMismatch = password && password2 && password !== password2;

  return (
    <FlexBox column gap={gap} fullWidth>
      <PasswordInput
        name="password"
        label={t ? t("common.field.password") : "Password"}
        {...commonProps}
        showToggle={!!password.length}
        InputProps={{
          onFocus: (e) => setAnchorEl(e.currentTarget as HTMLInputElement),
          onBlur: () => setAnchorEl(null),
        }}
        showHelperText={!!passwordError}
        error={!!passwordError}
        FormHelperTextProps={{className: classes.helperText}}
        helperText={
          passwordError ? (
            <Typography variant="h5" color="error">
              {passwordError}
            </Typography>
          ) : null
        }
        rules={{
          validate: (val: string) => {
            if (PASSWORD_UNSUPPORTED_CHARS_REGEX.test(val)) {
              return PASSWORD_UNSUPPORTED_CHARACTERS_ERROR;
            }
            if (val.length < MIN_PASSWORD_LENGTH) {
              return false;
            }
            return !PASSWORD_VALIDATION_REGEX.some(
              (pattern) => !pattern.test(val)
            );
          },
        }}
      />
      <Popper
        open={isPopperOpen}
        anchorEl={anchorEl}
        placement="right-start"
        className={classes.popper}
        style={{marginLeft: 24 + (password ? 48 : 0)}}
      >
        {renderPopperContent()}
      </Popper>
      <PasswordInput
        name="confirmPassword"
        label={t ? t("common.field.confirm_password") : "Confirm password"}
        {...commonProps}
        showToggle={!!password2.length}
        rules={{validate: (val: string) => val === password}}
      />
      {isPasswordsMismatch && (
        <Alert severity="error" fullWidth>
          {t
            ? t("common.validation.passwords_do_not_match")
            : "Passwords do not match"}
        </Alert>
      )}
    </FlexBox>
  );
};

export default PasswordInputs;
